// src/pages/CoverLetterPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useAIWorkflow } from "../contexts/AIWorkflowContext";
import PdfEditorComponent from "../components/dashboard/ai_flow/PdfEditorComponent";
import html2pdf from "html2pdf.js";
import { templates } from "../components/dashboard/ai_flow/resumeTemplateStyles";

// Styles
import "../components/dashboard/ai_flow/aiFlowStyles.css";
import "../components/dashboard/widgets/Widgets.css";

// A helper to generate styled HTML for any template type based on string content
const getStyledHtmlForExport = (content, templateId) => {
  const template = templates.find((t) => t.id === templateId) || templates[0];
  const styles = template.styles;
  return `<html><head><meta charset="UTF-8"><style>${styles}</style></head><body><div class="resume-template-container template-${template.id}">${content}</div></body></html>`;
};

function CoverLetterPage() {
  const navigate = useNavigate();
  const {
    coverLetter,
    isLoadingCoverLetter,
    coverLetterError,
    generateCoverLetterAction,
    selectedJobForOptimization,
    optimizedResume,
    aiSessionId,
  } = useAIWorkflow();

  // Prerequisite Check: Redirect if essential data is missing
  useEffect(() => {
    if (!selectedJobForOptimization || !optimizedResume) {
      navigate("/dashboard/resume-optimization", { replace: true });
    }
  }, [selectedJobForOptimization, optimizedResume, navigate]);

  // --- Template State ---
  const basicTemplates = templates.filter((t) => t.type === "basic");
  const premiumTemplates = templates.filter((t) => t.type === "premium");
  const [activeTemplateType, setActiveTemplateType] = useState("basic");
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    basicTemplates[0]?.id || templates[0].id
  );

  const [editedCoverLetter, setEditedCoverLetter] = useState("");
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState({
    state: "idle",
    message: "",
  });

  // --- Refs for Transitions ---
  const basicDropdownRef = useRef(null);
  const premiumDropdownRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (coverLetter) {
      setEditedCoverLetter((prev) =>
        prev === "" || prev === coverLetter ? coverLetter : prev
      );
    }
  }, [coverLetter]);

  const handleCoverLetterChange = useCallback((content) => {
    setEditedCoverLetter(content);
    setActionStatus({ state: "idle", message: "" });
  }, []);

  const handleTemplateTypeChange = (type) => {
    setActiveTemplateType(type);
    if (type === "basic" && basicTemplates.length > 0) {
      setSelectedTemplateId(basicTemplates[0].id);
    } else if (type === "premium" && premiumTemplates.length > 0) {
      setSelectedTemplateId(premiumTemplates[0].id);
    }
  };

  const handleTemplateSelect = (event) => {
    setSelectedTemplateId(event.target.value);
  };

  const getContentForAction = () => {
    const content = editedCoverLetter || coverLetter;
    if (content && content.replace(/<[^>]*>/g, "").trim().length >= 20) {
      return content;
    }
    setActionStatus({
      state: "error",
      message: "Cannot process empty/short cover letter.",
    });
    setTimeout(() => setActionStatus({ state: "idle", message: "" }), 3000);
    return null;
  };

  const isActionDisabled = () => {
    if (isActionInProgress) return true;
    const contentToCheck = editedCoverLetter || coverLetter;
    return (
      !contentToCheck ||
      contentToCheck.replace(/<[^>]*>/g, "").trim().length < 20
    );
  };

  const genericDownload = (blob, filename) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const performTextDocAction = (fileType) => {
    const contentToProcess = getContentForAction();
    if (!contentToProcess) return;

    const suggestedName = `CoverLetter-${
      selectedJobForOptimization?.companyName?.replace(/\s+/g, "_") || "Company"
    }`;
    const finalName = window.prompt(
      "Enter filename for download:",
      suggestedName
    );
    if (!finalName || !finalName.trim()) return;

    let blob;
    if (fileType === "txt") {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = contentToProcess;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";
      blob = new Blob([textContent], { type: "text/plain;charset=utf-8;" });
    } else {
      // doc
      const styledHtml = getStyledHtmlForExport(
        contentToProcess,
        selectedTemplateId
      );
      blob = new Blob(["\ufeff", styledHtml], { type: "application/msword" });
    }

    genericDownload(
      blob,
      `${finalName.trim()}.${fileType === "txt" ? "txt" : "doc"}`
    );
  };

  const handleDownloadPdf = async () => {
    const contentToDownload = getContentForAction();
    if (!contentToDownload) return;

    const suggestedName = `CoverLetter-${
      selectedJobForOptimization?.companyName?.replace(/\s+/g, "_") || "Company"
    }`;
    const finalName = window.prompt(
      "Enter filename for cover letter PDF:",
      suggestedName
    );
    if (!finalName || !finalName.trim()) return;

    setIsActionInProgress(true);
    setActionStatus({ state: "processing", message: "Generating PDF..." });

    try {
      const styledHtml = getStyledHtmlForExport(
        contentToDownload,
        selectedTemplateId
      );
      const options = {
        margin: 0,
        filename: `${finalName.trim()}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      await html2pdf().from(styledHtml).set(options).save();
      setActionStatus({ state: "success", message: "Download started!" });
      setTimeout(() => setActionStatus({ state: "idle", message: "" }), 2000);
    } catch (err) {
      console.error("Download CL PDF Error:", err);
      setActionStatus({
        state: "error",
        message: err.message || "Failed to generate PDF.",
      });
    } finally {
      setIsActionInProgress(false);
    }
  };

  if (!selectedJobForOptimization || !optimizedResume) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading or redirecting...
      </div>
    );
  }

  return (
    <div className="cover-letter-page p-4 md:p-6 lg:p-8 space-y-6 max-w-full mx-auto">
      <div className="text-center pr-8">
        <p className="text-md text-gray-600 mt-1">
          Craft a professional cover letter for{" "}
          <strong>{selectedJobForOptimization.jobTitle}</strong> at{" "}
          <strong>{selectedJobForOptimization.companyName}</strong>.
        </p>
      </div>

      {!coverLetter && !isLoadingCoverLetter && !coverLetterError && (
        <div className="widget-card text-center py-8">
          <p className="text-gray-600 mb-4">
            Click the button below to generate a tailored cover letter.
          </p>
          <button
            onClick={generateCoverLetterAction}
            className="px-6 py-2.5 rounded-md text-sm font-medium text-white bg-emerald-800 hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-700"
          >
            Generate Cover Letter
          </button>
        </div>
      )}

      {isLoadingCoverLetter && (
        <div className="p-6 text-center animate-pulse">
          Generating Cover Letter...
        </div>
      )}
      {coverLetterError && (
        <p className="text-red-600 bg-red-50 p-3 rounded-md">
          {coverLetterError}
        </p>
      )}

      {coverLetter && !isLoadingCoverLetter && !coverLetterError && (
        <>
          <div className="template-header-container">
            <div
              className={`template-selector-group ${
                activeTemplateType === "basic" ? "is-active" : ""
              }`}
            >
              <label onClick={() => handleTemplateTypeChange("basic")}>
                Cover Letter Templates
              </label>
              <CSSTransition
                nodeRef={basicDropdownRef}
                in={activeTemplateType === "basic"}
                timeout={300}
                classNames="dropdown-fluid-fade"
                unmountOnExit
              >
                <select
                  ref={basicDropdownRef}
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  className="template-dropdown-select"
                >
                  {basicTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </CSSTransition>
            </div>
            <div
              className={`template-selector-group ${
                activeTemplateType === "premium" ? "is-active" : ""
              }`}
            >
              <label onClick={() => handleTemplateTypeChange("premium")}>
                Premium Templates
              </label>
              <CSSTransition
                nodeRef={premiumDropdownRef}
                in={activeTemplateType === "premium"}
                timeout={300}
                classNames="dropdown-fluid-fade"
                unmountOnExit
              >
                <select
                  ref={premiumDropdownRef}
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  className="template-dropdown-select"
                >
                  {premiumTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </CSSTransition>
            </div>
          </div>

          <div className="widget-card p-0 overflow-hidden relative">
            <TransitionGroup className="content-transition-group">
              <CSSTransition
                nodeRef={contentRef}
                key={selectedTemplateId}
                timeout={300}
                classNames="content-fade"
              >
                <div className="content-transition-item" ref={contentRef}>
                  <PdfEditorComponent
                    key={`cover-${selectedTemplateId}-${
                      aiSessionId || "nokey"
                    }`}
                    initialContent={coverLetter}
                    isLoading={false}
                    onContentChange={handleCoverLetterChange}
                    templateId={selectedTemplateId}
                  />
                </div>
              </CSSTransition>
            </TransitionGroup>
            <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-end gap-3">
              {actionStatus.message && (
                <span
                  className={`text-sm font-medium order-first sm:order-none w-full sm:w-auto text-center sm:text-right ${
                    actionStatus.state === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {actionStatus.message}
                </span>
              )}
              <button
                onClick={() => performTextDocAction("txt")}
                className="opt-page-btn opt-page-btn-secondary"
                disabled={isActionDisabled()}
              >
                Download TXT
              </button>
              <button
                onClick={() => performTextDocAction("doc")}
                className="opt-page-btn opt-page-btn-secondary"
                disabled={isActionDisabled()}
              >
                Download DOC
              </button>
              <button
                onClick={handleDownloadPdf}
                className="opt-page-btn opt-page-btn-primary"
                disabled={isActionDisabled() || isActionInProgress}
              >
                {isActionInProgress ? "Generating..." : "Download PDF"}
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center flex-wrap items-center p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
            {selectedJobForOptimization.jobLink && (
              <a
                href={selectedJobForOptimization.jobLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2.5 rounded-md text-sm font-medium text-white bg-emerald-800 hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 flex items-center justify-center no-underline"
              >
                Apply Now
                <svg
                  className="h-4 w-4 ml-1.5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            )}
            <button
              onClick={() => navigate("/dashboard/job-listing")}
              className="px-6 py-2.5 rounded-md text-sm font-medium border border-emerald-700 text-emerald-800 bg-white hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
            >
              Back to Job Listings
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CoverLetterPage;
